import Decimal from 'decimal.js';
import { z } from 'zod';

Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

const decimalStringSchema = z.string().refine((value) => {
  try {
    return new Decimal(value).isFinite();
  } catch {
    return false;
  }
}, 'Invalid decimal string');

const formulaLineSchema = z.object({
  id: z.string().min(1),
  required: z.boolean(),
  amount: decimalStringSchema,
  unit: z.enum(['percent', 'kg', 'g', 'mg', 'L', 'mL', 'unit', 'piece', 'capsule', 'sachet']),
  assay: decimalStringSchema.optional(),
});

const formulaSchema = z.object({
  basis: z.enum([
    'weight_percent',
    'volume_percent',
    'mg_per_unit',
    'g_per_serving',
    'quantity_per_batch',
  ]),
  declaredFill: decimalStringSchema,
  declaredFillUnit: z.enum(['kg', 'g', 'mg', 'L', 'mL', 'unit']),
  density: decimalStringSchema.optional(),
  lines: z.array(formulaLineSchema),
  tolerance: decimalStringSchema.optional(),
});

export type FormulaInput = z.infer<typeof formulaSchema>;

export interface FormulaIssue {
  readonly code:
    | 'formula_sum'
    | 'negative_quantity'
    | 'duplicate_ingredient'
    | 'invalid_assay'
    | 'incompatible_unit'
    | 'missing_density'
    | 'missing_required_ingredient';
  readonly lineId?: string;
  readonly message: string;
}

export interface FormulaValidationResult {
  readonly valid: boolean;
  readonly issues: FormulaIssue[];
}

export function validateFormula(input: FormulaInput): FormulaValidationResult {
  const formula = formulaSchema.parse(input);
  const issues: FormulaIssue[] = [];
  const seen = new Set<string>();

  for (const line of formula.lines) {
    const amount = new Decimal(line.amount);
    if (amount.isNegative()) {
      issues.push({
        code: 'negative_quantity',
        lineId: line.id,
        message: 'Quantity cannot be negative',
      });
    }
    if (line.required && amount.isZero()) {
      issues.push({
        code: 'missing_required_ingredient',
        lineId: line.id,
        message: 'Required ingredient must have a positive quantity',
      });
    }
    if (seen.has(line.id)) {
      issues.push({
        code: 'duplicate_ingredient',
        lineId: line.id,
        message: 'Duplicate ingredient identity',
      });
    }
    seen.add(line.id);
    if (line.assay !== undefined) {
      const assay = new Decimal(line.assay);
      if (assay.lessThanOrEqualTo(0) || assay.greaterThan(1)) {
        issues.push({
          code: 'invalid_assay',
          lineId: line.id,
          message: 'Assay must be above 0 and at most 1',
        });
      }
    }
  }

  if (formula.basis === 'weight_percent' || formula.basis === 'volume_percent') {
    const total = Decimal.sum(...formula.lines.map((line) => new Decimal(line.amount)));
    const tolerance = new Decimal(formula.tolerance ?? '0.01');
    if (total.minus(100).abs().greaterThan(tolerance)) {
      issues.push({
        code: 'formula_sum',
        message: `Formula total is ${total.toString()}%, expected 100%`,
      });
    }
    const expectedUnit = 'percent';
    for (const line of formula.lines) {
      if (line.unit !== expectedUnit) {
        issues.push({
          code: 'incompatible_unit',
          lineId: line.id,
          message: 'Percentage formula requires percent lines',
        });
      }
    }
  }

  const hasMassVolumeMix =
    formula.lines.some((line) => ['kg', 'g', 'mg'].includes(line.unit)) &&
    formula.lines.some((line) => ['L', 'mL'].includes(line.unit));
  if (hasMassVolumeMix && formula.density === undefined) {
    issues.push({
      code: 'missing_density',
      message: 'Density is required for mass-volume conversion',
    });
  }

  return { valid: issues.length === 0, issues };
}

export type ConvertibleUnit = 'kg' | 'g' | 'mg' | 'L' | 'mL';

const unitFactors: Readonly<
  Record<ConvertibleUnit, { dimension: 'mass' | 'volume'; factor: Decimal }>
> = {
  kg: { dimension: 'mass', factor: new Decimal(1_000_000) },
  g: { dimension: 'mass', factor: new Decimal(1_000) },
  mg: { dimension: 'mass', factor: new Decimal(1) },
  L: { dimension: 'volume', factor: new Decimal(1_000) },
  mL: { dimension: 'volume', factor: new Decimal(1) },
};

export function convertUnit(value: string, from: ConvertibleUnit, to: ConvertibleUnit): string {
  if (unitFactors[from].dimension !== unitFactors[to].dimension) {
    throw new Error(`Cannot convert ${from} to ${to} without density`);
  }
  return new Decimal(value)
    .times(unitFactors[from].factor)
    .dividedBy(unitFactors[to].factor)
    .toString();
}

export interface ScaleFormulaInput {
  readonly saleableOutput: string;
  readonly outputUnit: ConvertibleUnit;
  readonly expectedYield: string;
  readonly lines: readonly {
    readonly id: string;
    readonly fraction: string;
    readonly assay?: string;
    readonly overfillRate?: string;
    readonly processLossRate?: string;
  }[];
}

export interface ScaledFormulaLine {
  readonly id: string;
  readonly unit: ConvertibleUnit;
  readonly netRequired: string;
  readonly assayAdjustedRequired: string;
  readonly grossRequired: string;
}

function decimalOutput(value: Decimal): string {
  return value.toDecimalPlaces(20).toString();
}

export function scaleFormula(input: ScaleFormulaInput): ScaledFormulaLine[] {
  const yieldFraction = new Decimal(input.expectedYield);
  if (yieldFraction.lessThanOrEqualTo(0) || yieldFraction.greaterThan(1)) {
    throw new Error('Expected yield must be above 0 and at most 1');
  }

  return input.lines.map((line) => {
    const fraction = new Decimal(line.fraction);
    const assay = new Decimal(line.assay ?? 1);
    const overfillRate = new Decimal(line.overfillRate ?? 0);
    const processLossRate = new Decimal(line.processLossRate ?? 0);
    if (assay.lessThanOrEqualTo(0) || assay.greaterThan(1)) {
      throw new Error(`Invalid assay for ${line.id}`);
    }
    if (fraction.isNegative() || overfillRate.isNegative() || processLossRate.isNegative()) {
      throw new Error(`Negative scaling input for ${line.id}`);
    }
    const net = new Decimal(input.saleableOutput).times(fraction);
    const assayAdjusted = net.dividedBy(assay);
    const gross = assayAdjusted
      .times(overfillRate.plus(1))
      .dividedBy(yieldFraction)
      .times(processLossRate.plus(1));
    return {
      id: line.id,
      unit: input.outputUnit,
      netRequired: decimalOutput(net),
      assayAdjustedRequired: decimalOutput(assayAdjusted),
      grossRequired: decimalOutput(gross),
    };
  });
}

export function compatibleConstituent(
  blendPercent: string,
  concentrationPercent: string,
  blendBasis: string,
  concentrationBasis: string,
): string {
  if (!['mass', 'volume'].includes(blendBasis) || !['mass', 'volume'].includes(concentrationBasis))
    throw new Error('unknown_basis');
  if (blendBasis !== concentrationBasis) throw new Error('incompatible_basis');
  const blend = new Decimal(blendPercent),
    concentration = new Decimal(concentrationPercent);
  if (
    !blend.isFinite() ||
    !concentration.isFinite() ||
    blend.lt(0) ||
    blend.gt(100) ||
    concentration.lt(0) ||
    concentration.gt(100)
  )
    throw new Error('invalid_percentage');
  return blend.times(concentration).div(100).toFixed();
}
export function productionQuantity(
  units: string,
  millilitres: string,
  densityKgPerLitre?: string,
): { litres: string; kilograms: string | null; engineVersion: 'oem-quantity-v2' } {
  const qty = new Decimal(units),
    fill = new Decimal(millilitres);
  if (!qty.isFinite() || !fill.isFinite() || qty.lte(0) || fill.lte(0))
    throw new Error('invalid_quantity');
  const litres = qty.times(fill).div(1000);
  if (densityKgPerLitre === undefined)
    return { litres: litres.toFixed(), kilograms: null, engineVersion: 'oem-quantity-v2' };
  const density = new Decimal(densityKgPerLitre);
  if (!density.isFinite() || density.lte(0)) throw new Error('invalid_density');
  return {
    litres: litres.toFixed(),
    kilograms: litres.times(density).toFixed(),
    engineVersion: 'oem-quantity-v2',
  };
}
export * from './development';
